import { i18n } from '@/shared/i18n';
import googleAdsLogo from '@/assets/connectors/googleAds_logo.png';
import metaAdsLogo from '@/assets/connectors/metaAds_logo.png';
import linkedInAdsLogo from '@/assets/connectors/linkedInAds_logo.png';
import display360Logo from '@/assets/connectors/display360_logo.png';
import campaignManager360Logo from '@/assets/connectors/campaignManager360_logo.png';
import tradeDeskLogo from '@/assets/connectors/tradeDesk_logo.png';
import amazonDspLogo from '@/assets/connectors/amazonDSP_logo.png';
import tiktokAdsLogo from '@/assets/connectors/tiktokAds_logo.svg';
import microsoftAdsLogo from '@/assets/connectors/microsoftAds_logo.svg';
import youtubeAdsLogo from '@/assets/connectors/youtubeAds_logo.svg';
import walmartConnectLogo from '@/assets/connectors/walmartConnect_logo.svg';

export type ConnectorPlatform =
  | 'google_ads'
  | 'meta_ads'
  | 'linkedin_ads'
  | 'dv360'
  | 'cm360'
  | 'the_trade_desk'
  | 'amazon_dsp'
  | 'amazon_ads'
  | 'tiktok_ads'
  | 'microsoft_ads'
  | 'youtube_ads'
  | 'walmart_connect';

export type ConnectorStatus = 'pending' | 'connected' | 'disconnected' | 'expired' | 'error';

export type AuthMethod = 'oauth' | 'system_user_token' | 'service_account';

export type SyncCadence = 'every_6h' | 'every_12h' | 'daily' | 'manual';

export interface MediaConnector {
  id: string;
  workspaceId: string;
  platform: ConnectorPlatform;
  status: ConnectorStatus;
  authMethod: AuthMethod;
  title: string;
  tags: string[];
  syncCadence: SyncCadence;
  externalAccountId?: string | null;
  externalAccountName?: string | null;
  /** Who the ads are for and who pays, as the EU Digital Services Act requires. */
  dsaBeneficiary?: string | null;
  dsaPayor?: string | null;
  /** The advertiser this connection buys for, where the platform nests them under the account. */
  advertiserId?: string | null;
  advertiserName?: string | null;
  tokenExpiresAt?: string | null;
  connectedAt?: string | null;
  connectedBy: string;
  errorMessage?: string | null;
  createdAt: string;
  updatedAt: string;
  /** Optional so a cached row written before capabilities existed still parses. */
  capabilities?: ConnectorCapabilities;
}

/**
 * How a capability last answered.
 *
 * `unknown` is an absent answer, never a negative one — a capability nothing has
 * exercised is not a broken one. It has to read as grey, not amber: an account nobody has
 * published through yet must not look like it needs attention.
 */
export type CapabilityState = 'unknown' | 'ok' | 'failing';

export interface ConnectorCapability {
  state: CapabilityState;
  checkedAt: string | null;
  /** The platform's own words. Google's names the cloud project and carries the fix URL. */
  error: string | null;
}

/**
 * The three things a connection can be asked to do, reported separately, because they go
 * through different APIs and fail independently.
 */
export interface ConnectorCapabilities {
  signIn: ConnectorCapability;
  publishing: ConnectorCapability;
  reporting: ConnectorCapability;
}

// Getters, not values, in every label and description table below: converting one to a plain
// value freezes its copy at import.
export const CAPABILITY_LABELS: Record<keyof ConnectorCapabilities, string> = {
  get signIn() {
    return i18n.t('knowledge:mediaConnector.types.capability.signIn');
  },
  get publishing() {
    return i18n.t('knowledge:mediaConnector.types.capability.publishing');
  },
  get reporting() {
    return i18n.t('knowledge:mediaConnector.types.capability.reporting');
  },
};

/** Grey for unknown, deliberately. See {@link CapabilityState}. */
export const CAPABILITY_STATE_VARIANT: Record<CapabilityState, ConnectorStatusVariant> = {
  ok: 'success',
  failing: 'destructive',
  unknown: 'muted',
};

export const CAPABILITY_STATE_LABELS: Record<CapabilityState, string> = {
  get ok() {
    return i18n.t('knowledge:mediaConnector.types.capabilityState.ok');
  },
  get failing() {
    return i18n.t('knowledge:mediaConnector.types.capabilityState.failing');
  },
  get unknown() {
    return i18n.t('knowledge:mediaConnector.types.capabilityState.unknown');
  },
};

export interface ListConnectorsResponse {
  items: MediaConnector[];
}

/**
 * A platform the current workspace is allowed to use, as returned by
 * `GET /media-connectors/platforms`. The backend already filters this list by each
 * platform's `media_connector_<platform>` feature flag, so the connectors surface
 * renders only what this endpoint returns — the frontend never checks the flags
 * itself.
 */
export interface PlatformInfo {
  platform: ConnectorPlatform;
  label: string;
  description: string;
  category: PlatformCategory;
  authMethods: AuthMethod[];
  connectedAccountCount: number;
  supportsBrowsing: boolean;
}

export interface ListPlatformsResponse {
  items: PlatformInfo[];
}

export type SyncJobStatus = 'idle' | 'running' | 'completed' | 'failed';

export interface SyncJobStatusResponse {
  id: string;
  connectorId: string;
  status: SyncJobStatus;
  lastSyncAt: string | null;
  syncWindowStart: string | null;
  syncWindowEnd: string | null;
  lastError: string | null;
  entriesSynced: number;
}

export interface ListSyncJobStatusResponse {
  items: SyncJobStatusResponse[];
}

export const SYNC_STATUS_LABELS: Record<SyncJobStatus, string> = {
  get idle() {
    return i18n.t('knowledge:mediaConnector.types.syncStatus.idle');
  },
  get running() {
    return i18n.t('knowledge:mediaConnector.types.syncStatus.running');
  },
  get completed() {
    return i18n.t('knowledge:mediaConnector.types.syncStatus.completed');
  },
  get failed() {
    return i18n.t('knowledge:mediaConnector.types.syncStatus.failed');
  },
};

export const SYNC_STATUS_VARIANT: Record<SyncJobStatus, ConnectorStatusVariant> = {
  idle: 'neutral',
  running: 'neutral',
  completed: 'success',
  failed: 'destructive',
};

export const CONNECTOR_PLATFORM_ICONS: Record<ConnectorPlatform, string> = {
  google_ads: googleAdsLogo,
  meta_ads: metaAdsLogo,
  linkedin_ads: linkedInAdsLogo,
  dv360: display360Logo,
  cm360: campaignManager360Logo,
  the_trade_desk: tradeDeskLogo,
  amazon_dsp: amazonDspLogo,
  amazon_ads: amazonDspLogo,
  tiktok_ads: tiktokAdsLogo,
  microsoft_ads: microsoftAdsLogo,
  youtube_ads: youtubeAdsLogo,
  walmart_connect: walmartConnectLogo,
};

export const CONNECTOR_PLATFORM_LABELS: Record<ConnectorPlatform, string> = {
  get google_ads() {
    return i18n.t('knowledge:mediaConnector.types.platform.googleAds');
  },
  get meta_ads() {
    return i18n.t('knowledge:mediaConnector.types.platform.metaAds');
  },
  get linkedin_ads() {
    return i18n.t('knowledge:mediaConnector.types.platform.linkedinAds');
  },
  get dv360() {
    return i18n.t('knowledge:mediaConnector.types.platform.dv360');
  },
  get cm360() {
    return i18n.t('knowledge:mediaConnector.types.platform.campaignManager360');
  },
  get the_trade_desk() {
    return i18n.t('knowledge:mediaConnector.types.platform.theTradeDesk');
  },
  get amazon_dsp() {
    return i18n.t('knowledge:mediaConnector.types.platform.amazonDsp');
  },
  get amazon_ads() {
    return i18n.t('knowledge:mediaConnector.types.platform.amazonAds');
  },
  get tiktok_ads() {
    return i18n.t('knowledge:mediaConnector.types.platform.tiktokAds');
  },
  get microsoft_ads() {
    return i18n.t('knowledge:mediaConnector.types.platform.microsoftAds');
  },
  get youtube_ads() {
    return i18n.t('knowledge:mediaConnector.types.platform.youtubeAds');
  },
  get walmart_connect() {
    return i18n.t('knowledge:mediaConnector.types.platform.walmartConnect');
  },
};

/**
 * The label for a platform key from the wire or the URL, which may be one this app does not
 * know yet: that key is shown as it is.
 */
export function connectorPlatformLabel(platform: string): string {
  return Object.hasOwn(CONNECTOR_PLATFORM_LABELS, platform)
    ? CONNECTOR_PLATFORM_LABELS[platform as ConnectorPlatform]
    : platform;
}

export const CONNECTOR_PLATFORM_DESCRIPTIONS: Record<ConnectorPlatform, string> = {
  get google_ads() {
    return i18n.t('knowledge:mediaConnector.types.platformDescription.googleAds');
  },
  get meta_ads() {
    return i18n.t('knowledge:mediaConnector.types.platformDescription.metaAds');
  },
  get linkedin_ads() {
    return i18n.t('knowledge:mediaConnector.types.platformDescription.linkedinAds');
  },
  get dv360() {
    return i18n.t('knowledge:mediaConnector.types.platformDescription.dv360');
  },
  get cm360() {
    return i18n.t('knowledge:mediaConnector.types.platformDescription.campaignManager360');
  },
  get the_trade_desk() {
    return i18n.t('knowledge:mediaConnector.types.platformDescription.theTradeDesk');
  },
  get amazon_dsp() {
    return i18n.t('knowledge:mediaConnector.types.platformDescription.amazonDsp');
  },
  get amazon_ads() {
    return i18n.t('knowledge:mediaConnector.types.platformDescription.amazonAds');
  },
  get tiktok_ads() {
    return i18n.t('knowledge:mediaConnector.types.platformDescription.tiktokAds');
  },
  get microsoft_ads() {
    return i18n.t('knowledge:mediaConnector.types.platformDescription.microsoftAds');
  },
  get youtube_ads() {
    return i18n.t('knowledge:mediaConnector.types.platformDescription.youtubeAds');
  },
  get walmart_connect() {
    return i18n.t('knowledge:mediaConnector.types.platformDescription.walmartConnect');
  },
};

/**
 * Sub-categories used to group platforms within the Paid Media & Ad Intelligence
 * page. Mirrors the backend `PlatformCategory` enum (media-api) so the two
 * stay consistent. `retail_media` groups Amazon Ads and Walmart Connect.
 *
 * `ad_serving` is deliberately separate from `programmatic`: an ad server traffics
 * and measures media that was bought elsewhere, so grouping it with the DSPs would
 * imply CM360 can spend a budget, which it cannot.
 */
export type PlatformCategory = 'search_social' | 'programmatic' | 'retail_media' | 'ad_serving';

export const CONNECTOR_PLATFORM_CATEGORY: Record<ConnectorPlatform, PlatformCategory> = {
  google_ads: 'search_social',
  meta_ads: 'search_social',
  tiktok_ads: 'search_social',
  linkedin_ads: 'search_social',
  microsoft_ads: 'search_social',
  youtube_ads: 'search_social',
  dv360: 'programmatic',
  the_trade_desk: 'programmatic',
  amazon_dsp: 'programmatic',
  amazon_ads: 'retail_media',
  walmart_connect: 'retail_media',
  cm360: 'ad_serving',
};

export const PLATFORM_CATEGORY_LABELS: Record<PlatformCategory, string> = {
  get search_social() {
    return i18n.t('knowledge:mediaConnector.types.category.searchSocial');
  },
  get programmatic() {
    return i18n.t('knowledge:mediaConnector.types.category.programmatic');
  },
  get retail_media() {
    return i18n.t('knowledge:mediaConnector.types.category.retailMedia');
  },
  get ad_serving() {
    return i18n.t('knowledge:mediaConnector.types.category.adServing');
  },
};

export const PLATFORM_CATEGORY_DESCRIPTIONS: Record<PlatformCategory, string> = {
  get search_social() {
    return i18n.t('knowledge:mediaConnector.types.categoryDescription.searchSocial');
  },
  get programmatic() {
    return i18n.t('knowledge:mediaConnector.types.categoryDescription.programmatic');
  },
  get retail_media() {
    return i18n.t('knowledge:mediaConnector.types.categoryDescription.retailMedia');
  },
  get ad_serving() {
    return i18n.t('knowledge:mediaConnector.types.categoryDescription.adServing');
  },
};

/** Display order of the categories within the page. */
export const PLATFORM_CATEGORY_ORDER: PlatformCategory[] = [
  'search_social',
  'programmatic',
  'retail_media',
  'ad_serving',
];

export const PLATFORM_AUTH_METHODS: Record<ConnectorPlatform, AuthMethod[]> = {
  google_ads: ['oauth', 'service_account'],
  meta_ads: ['oauth', 'system_user_token'],
  dv360: ['oauth', 'service_account'],
  cm360: ['oauth', 'service_account'],
  linkedin_ads: ['oauth'],
  the_trade_desk: ['oauth'],
  amazon_dsp: ['oauth'],
  amazon_ads: ['oauth'],
  tiktok_ads: ['oauth'],
  microsoft_ads: ['oauth'],
  youtube_ads: ['oauth', 'service_account'],
  walmart_connect: ['oauth'],
};

export const AUTH_METHOD_LABELS: Record<AuthMethod, string> = {
  get oauth() {
    return i18n.t('knowledge:mediaConnector.types.authMethod.oauth');
  },
  get system_user_token() {
    return i18n.t('knowledge:mediaConnector.types.authMethod.systemUserToken');
  },
  get service_account() {
    return i18n.t('knowledge:mediaConnector.types.authMethod.serviceAccount');
  },
};

export const AUTH_METHOD_DESCRIPTIONS: Record<AuthMethod, string> = {
  get oauth() {
    return i18n.t('knowledge:mediaConnector.types.authMethodDescription.oauth');
  },
  get system_user_token() {
    return i18n.t('knowledge:mediaConnector.types.authMethodDescription.systemUserToken');
  },
  get service_account() {
    return i18n.t('knowledge:mediaConnector.types.authMethodDescription.serviceAccount');
  },
};

export const CONNECTOR_STATUS_LABELS: Record<ConnectorStatus, string> = {
  get pending() {
    return i18n.t('knowledge:mediaConnector.types.status.pending');
  },
  get connected() {
    return i18n.t('knowledge:mediaConnector.types.status.connected');
  },
  get disconnected() {
    return i18n.t('knowledge:mediaConnector.types.status.disconnected');
  },
  get expired() {
    return i18n.t('knowledge:mediaConnector.types.status.expired');
  },
  get error() {
    return i18n.t('knowledge:mediaConnector.types.status.error');
  },
};

export type ConnectorStatusVariant = 'neutral' | 'success' | 'warning' | 'muted' | 'destructive';

export const CONNECTOR_STATUS_VARIANT: Record<ConnectorStatus, ConnectorStatusVariant> = {
  pending: 'neutral',
  connected: 'success',
  disconnected: 'muted',
  expired: 'warning',
  error: 'destructive',
};

export const SYNC_CADENCE_OPTIONS: SyncCadence[] = ['every_6h', 'every_12h', 'daily', 'manual'];

export const SYNC_CADENCE_LABELS: Record<SyncCadence, string> = {
  get every_6h() {
    return i18n.t('knowledge:mediaConnector.types.syncCadence.every6h');
  },
  get every_12h() {
    return i18n.t('knowledge:mediaConnector.types.syncCadence.every12h');
  },
  get daily() {
    return i18n.t('knowledge:mediaConnector.types.syncCadence.daily');
  },
  get manual() {
    return i18n.t('knowledge:mediaConnector.types.syncCadence.manual');
  },
};

export const SYNC_CADENCE_DESCRIPTIONS: Record<SyncCadence, string> = {
  get every_6h() {
    return i18n.t('knowledge:mediaConnector.types.syncCadenceDescription.every6h');
  },
  get every_12h() {
    return i18n.t('knowledge:mediaConnector.types.syncCadenceDescription.every12h');
  },
  get daily() {
    return i18n.t('knowledge:mediaConnector.types.syncCadenceDescription.daily');
  },
  get manual() {
    return i18n.t('knowledge:mediaConnector.types.syncCadenceDescription.manual');
  },
};

export const PLATFORM_DEFAULT_CADENCE: Record<ConnectorPlatform, SyncCadence> = {
  google_ads: 'daily',
  meta_ads: 'daily',
  linkedin_ads: 'daily',
  dv360: 'daily',
  cm360: 'daily',
  the_trade_desk: 'daily',
  amazon_dsp: 'daily',
  amazon_ads: 'daily',
  tiktok_ads: 'daily',
  microsoft_ads: 'daily',
  youtube_ads: 'daily',
  walmart_connect: 'daily',
};

/**
 * Platforms whose connect stores an id that is not itself an advertiser, so one has to be
 * given before the connection can publish or report.
 *
 * DV360 alone: it connects to a partner, which buys nothing. Amazon DSP, The Trade Desk
 * and Walmart nest advertisers too but already store one, so theirs is corrected on the
 * account rather than demanded up front. Mirrors `needsAnAdvertiser` in media-api.
 */
export const PLATFORMS_REQUIRING_AN_ADVERTISER: ConnectorPlatform[] = ['dv360'];
